import { Box, DialogContent, MenuItem, Typography, Alert, Grid } from '@mui/material';
import { CustomSelect } from '../../../../../common/components';

/**
 * Tab de templates del proyecto
 * Muestra templates asignados en modo edición o permite seleccionar en modo creación
 */
const TemplatesTab = ({
  localProject,
  handleSelectChange,
  progressTemplates = [],
  qualityTemplates = [],
  editMode = false,
}) => {
  let assignedProgressTemplates = [];
  let assignedQualityTemplates = [];

  if (editMode && localProject?.project_task_assignments) {
    const assignedTemplateIds = localProject.project_task_assignments.map(
      (assignment) => assignment.uuid_task_template
    );

    assignedProgressTemplates = progressTemplates.filter((template) =>
      assignedTemplateIds.includes(template.uuid)
    );
    assignedQualityTemplates = qualityTemplates.filter((template) =>
      assignedTemplateIds.includes(template.uuid)
    );
  }

  const hasProgressTemplate = editMode
    ? assignedProgressTemplates.length > 0
    : localProject?.progress_template;
  const hasQualityTemplate = editMode
    ? assignedQualityTemplates.length > 0
    : localProject?.quality_template;

  return (
    <Box sx={{ width: '100%' }}>
      <DialogContent sx={{ display: 'flex' }}>
        <Box sx={{ width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', fontWeight: 'bold' }}>
            {editMode
              ? 'Plantillas Asignadas (Solo lectura)'
              : 'Selección de Plantillas (Obligatorio)'}
          </Typography>

          {editMode ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              En modo edición, las plantillas asignadas no pueden modificarse. Las tareas de
              seguimiento ya han sido generadas.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              Las plantillas son obligatorias para crear un proyecto. Al asignar las plantillas, se
              generarán automáticamente todas las tareas de seguimiento de progreso y control de
              calidad.
            </Alert>
          )}

          {editMode ? (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Plantillas de Progreso Asignadas:
              </Typography>
              {assignedProgressTemplates.length > 0 ? (
                assignedProgressTemplates.map((template) => (
                  <Box
                    key={template.uuid}
                    sx={{
                      mb: 1,
                      p: 2,
                      bgcolor: 'primary.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'primary.main',
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      ✓ {template.name}
                    </Typography>
                    {template.description && (
                      <Typography variant="caption" color="text.secondary">
                        {template.description}
                      </Typography>
                    )}
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No hay plantillas de progreso asignadas
                </Typography>
              )}

              <Typography variant="subtitle1" sx={{ mb: 2, mt: 3, fontWeight: 'bold' }}>
                Plantillas de Calidad Asignadas:
              </Typography>
              {assignedQualityTemplates.length > 0 ? (
                assignedQualityTemplates.map((template) => (
                  <Box
                    key={template.uuid}
                    sx={{
                      mb: 1,
                      p: 2,
                      bgcolor: 'success.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      ✓ {template.name}
                    </Typography>
                    {template.description && (
                      <Typography variant="caption" color="text.secondary">
                        {template.description}
                      </Typography>
                    )}
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No hay plantillas de calidad asignadas
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <CustomSelect
                    label="Template de Progreso *"
                    name="progress_template"
                    value={localProject?.progress_template || ''}
                    onChange={handleSelectChange}
                    size="medium"
                    required
                    fullWidth
                  >
                    <MenuItem value="">Seleccionar plantilla de progreso</MenuItem>
                    {progressTemplates.map((template) => (
                      <MenuItem key={template.uuid} value={template.uuid}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </Grid>

                <Grid item xs={12} md={6}>
                  <CustomSelect
                    label="Template de Calidad *"
                    name="quality_template"
                    value={localProject?.quality_template || ''}
                    onChange={handleSelectChange}
                    size="medium"
                    required
                    fullWidth
                  >
                    <MenuItem value="">Seleccionar plantilla de calidad</MenuItem>
                    {qualityTemplates.map((template) => (
                      <MenuItem key={template.uuid} value={template.uuid}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </Grid>

                <Grid item xs={12}>
                  {(hasProgressTemplate || hasQualityTemplate) && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: 'success.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'success.main',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 'bold', color: 'success.dark', mb: 1 }}
                      >
                        Plantillas Seleccionadas:
                      </Typography>
                      {hasProgressTemplate && (
                        <Typography variant="body2" color="success.dark">
                          ✓ Plantilla de Progreso:{' '}
                          {
                            progressTemplates.find((t) => t.uuid === localProject.progress_template)
                              ?.name
                          }
                        </Typography>
                      )}
                      {hasQualityTemplate && (
                        <Typography variant="body2" color="success.dark">
                          ✓ Plantilla de Calidad:{' '}
                          {
                            qualityTemplates.find((t) => t.uuid === localProject.quality_template)
                              ?.name
                          }
                        </Typography>
                      )}
                    </Box>
                  )}

                  {(!hasProgressTemplate || !hasQualityTemplate) && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: 'error.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'error.main',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 'bold', color: 'error.dark', mb: 1 }}
                      >
                        Plantillas Faltantes:
                      </Typography>
                      {!hasProgressTemplate && (
                        <Typography variant="body2" color="error.dark">
                          ✗ Debe seleccionar una plantilla de progreso
                        </Typography>
                      )}
                      {!hasQualityTemplate && (
                        <Typography variant="body2" color="error.dark">
                          ✗ Debe seleccionar una plantilla de calidad
                        </Typography>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Box>
  );
};

export default TemplatesTab;
